import type { IconName } from "@-/dev.improve.haniverse.components.ui.icon";
import { SectionAccordionStickyHeader } from "#section-accordion-sticky-header";
import { SectionAccordionContent } from "#section-accordion-content";
import { cn } from "@-/com.npmjs.cn";
import type { ReactNode } from "react";
import { View } from "@-/com.npmjs.react-native";
import type { SectionAccordionHeaderAction } from "#section-accordion-header-action";

export function SectionAccordionNodes({
  actions,
  bodyClassName,
  bodyKey,
  children,
  count,
  fallback,
  headerKey,
  iconName,
  isLoading,
  isOpen,
  isPlaceholder,
  onToggle,
  title,
  trailing,
}: {
  /** Trailing header toggles, drawn in place of the chevron. See `SectionAccordionStickyHeader`. */
  actions?: readonly SectionAccordionHeaderAction[];
  /** Override the default body styling (`bg-surface`). */
  bodyClassName?: string;
  bodyKey: string;
  children?: ReactNode;
  count?: number;
  /**
   * Shown while the open body's first mount is deferred (or while it suspends). Pass a skeleton
   * that mirrors the body's real layout; defaults to generic text lines.
   */
  fallback?: ReactNode;
  headerKey: string;
  iconName: IconName;
  /**
   * Swap the collapse/expand chevron for a small spinner and make the header inert. The
   * standard loading affordance for a section — prefer this over hand-passing
   * `trailing={<ActivityIndicator size="small" />}`. Ignored when `trailing` is set.
   */
  isLoading?: boolean;
  isOpen: boolean;
  /**
   * This section is a loading SKELETON with no real content behind it. Implies the spinner and
   * swaps the header's accessible label to `"Loading ${title}"` so a placeholder can never
   * satisfy an assertion (or screen reader) looking for the settled `"${title} section"` — see
   * `SectionAccordionStickyHeader.isPlaceholder`.
   */
  isPlaceholder?: boolean;
  /**
   * Called when the bottom of this section's OPEN body scrolls to within
   * `onEndReachedThreshold` viewport-heights of the bottom of the enclosing
   * `SectionAccordionTab` viewport — the section-level equivalent of a `FlatList`'s
   * `onEndReached`, for sections that render their content incrementally. Fires at most once per
   * body height: once the body grows (more content rendered) and is still near the bottom, it
   * fires again, so a body that doesn't fill the viewport keeps loading until it does. Only
   * fires inside a `SectionAccordionTab` (which owns the scroll position); a standalone
   * `SectionAccordionNodes` never calls it.
   */
  onEndReached?: () => void;
  /**
   * How far from the bottom of the viewport the body's end must be before `onEndReached` fires,
   * in multiples of the viewport height (same unit as `FlatList.onEndReachedThreshold`).
   * Defaults to `1` — one full viewport ahead, so a user scrolling at reading speed never sees
   * the section run out.
   */
  onEndReachedThreshold?: number;
  onToggle: () => void;
  title: string;
  trailing?: ReactNode;
}) {
  return (
    <>
      <SectionAccordionStickyHeader
        actions={actions}
        count={count}
        iconName={iconName}
        isLoading={isLoading}
        isOpen={isOpen}
        isPlaceholder={isPlaceholder}
        key={headerKey}
        onToggle={onToggle}
        title={title}
        trailing={trailing}
      />

      {isOpen ? (
        <View className={cn("border-b border-border bg-surface", bodyClassName)} key={bodyKey}>
          <SectionAccordionContent fallback={fallback} title={title}>
            {children}
          </SectionAccordionContent>
        </View>
      ) : null}
    </>
  );
}
